import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getApi } from '../providers/AuthContext';
import PageWrapper from '../components/PageWrapper';
import CalendarGrid from '../components/Calendar/CalendarGrid';
import EventFormModal from '../components/Calendar/EventFormModal';
import { Calendar, AlertCircle, X, Pencil, Trash2, Clock, MapPin, User, CheckCircle2, ArrowRight } from 'lucide-react';

const DEFAULT_EVENT_COLOR = '#4f46e5';

function toLocalDatetimeString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const CalendarPage: React.FC = () => {
  const api = getApi();
  const [events, setEvents] = useState<any[]>([]);
  const [children, setChildren] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any | null>(null);
  const [prefillDate, setPrefillDate] = useState<Date | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [calendarConnected, setCalendarConnected] = useState<boolean | null>(null);

  const loadEvents = async () => {
    try {
      setError(null);
      const result = await api.get('/api/v1/calendar_events');
      setEvents(result.calendar_events || []);
    } catch {
      setError('Failed to load calendar events.');
    }
  };

  const loadChildren = async () => {
    try {
      const result = await api.get('/api/v1/children');
      setChildren(result.children || []);
    } catch {
      // Children list is optional
    }
  };

  const loadCalendarStatus = async () => {
    try {
      const result = await api.get('/api/v1/calendars/status');
      setCalendarConnected(result.connected ?? false);
    } catch {
      setCalendarConnected(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([loadEvents(), loadChildren(), loadCalendarStatus()]);
      setLoading(false);
    };
    load();
  }, []);

  const handleCreate = () => {
    setEditingEvent(null);
    setPrefillDate(null);
    setShowModal(true);
  };

  const handleDayClick = (date: Date) => {
    setEditingEvent(null);
    setPrefillDate(date);
    setShowModal(true);
  };

  const handleEventClick = (event: any) => {
    setSelectedEvent(event);
  };

  const handleEditEvent = (event: any) => {
    setSelectedEvent(null);
    setEditingEvent(event);
    setShowModal(true);
  };

  const handleSave = async (data: Record<string, any>) => {
    if (editingEvent?.id) {
      await api.put(`/api/v1/calendar_events/${editingEvent.id}`, { calendar_event: data });
    } else {
      await api.post('/api/v1/calendar_events', { calendar_event: data });
    }
    setShowModal(false);
    setEditingEvent(null);
    setPrefillDate(null);
    await loadEvents();
  };

  const handleDeleteClick = (eventId: number) => {
    setSelectedEvent(null);
    setConfirmDeleteId(eventId);
  };

  const handleDeleteConfirm = async () => {
    if (confirmDeleteId === null) return;
    setDeletingId(confirmDeleteId);
    setConfirmDeleteId(null);
    try {
      await api.delete(`/api/v1/calendar_events/${confirmDeleteId}`);
      await loadEvents();
    } catch {
      setError('Failed to delete event.');
    } finally {
      setDeletingId(null);
    }
  };

  const getPrefillEvent = () => {
    if (editingEvent) return editingEvent;
    if (prefillDate) {
      const start = new Date(prefillDate);
      if (start.getHours() === 0 && start.getMinutes() === 0) {
        start.setHours(9, 0, 0, 0);
      }
      const end = new Date(start.getTime() + 3600000);
      return {
        title: '',
        description: '',
        start_time: toLocalDatetimeString(start),
        end_time: toLocalDatetimeString(end),
        location: '',
        color: DEFAULT_EVENT_COLOR,
        child_id: '',
      };
    }
    return null;
  };

  return (
    <PageWrapper pageTitle="Calendar">
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
        {/* Google Calendar sync banner */}
        <AnimatePresence>
          {calendarConnected !== null && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className={`flex items-center gap-3 text-sm px-4 py-3.5 rounded-xl mb-5 border transition-all ${
                calendarConnected
                  ? 'bg-success/[0.04] dark:bg-success/[0.08] border-success/20 text-success'
                  : 'bg-muted/50 dark:bg-muted/30 border-border/60 text-muted-foreground'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                calendarConnected
                  ? 'bg-success/10'
                  : 'bg-muted'
              }`}>
                {calendarConnected ? (
                  <CheckCircle2 className="w-4 h-4 text-success" />
                ) : (
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1">
                <span className="font-semibold text-sm">
                  {calendarConnected ? 'Google Calendar synced' : 'Google Calendar not connected'}
                </span>
                {!calendarConnected && (
                  <p className="text-xs text-muted-foreground/70 mt-0.5 m-0">
                    Connect to sync events automatically
                  </p>
                )}
              </div>
              {!calendarConnected && (
                <a href="/account" className="inline-flex items-center gap-1 text-primary hover:text-primary-hover font-semibold text-sm transition-colors">
                  Connect
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              )}
              {calendarConnected && (
                <span className="w-2 h-2 rounded-full bg-success animate-pulse-soft flex-shrink-0" />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex items-center gap-3 p-4 bg-destructive/[0.06] dark:bg-destructive/[0.12] border border-destructive/20 text-destructive rounded-xl mb-5"
            >
              <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium flex-1">{error}</span>
              <button onClick={() => setError(null)} className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading skeleton */}
        {loading ? (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="skeleton w-20 h-9 rounded-xl" />
                <div className="skeleton w-44 h-6 rounded-lg ml-2" />
              </div>
              <div className="flex items-center gap-2.5">
                <div className="skeleton w-20 h-9 rounded-xl" />
                <div className="skeleton w-48 h-9 rounded-xl" />
                <div className="skeleton w-24 h-9 rounded-xl" />
              </div>
            </div>
            <div className="border border-border/60 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-7 border-b border-border/60 bg-muted/30">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="py-3 flex justify-center border-r border-border/40 last:border-r-0">
                    <div className="skeleton w-8 h-3 rounded" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {Array.from({ length: 35 }).map((_, i) => (
                  <div key={i} className="min-h-[110px] border-b border-r border-border/60 p-2 bg-card">
                    <div className="skeleton w-7 h-7 rounded-lg mb-2" />
                    {i % 5 === 0 && <div className="skeleton w-full h-5 rounded-lg mb-1" />}
                    {i % 7 === 2 && <div className="skeleton w-3/4 h-5 rounded-lg" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : events.length === 0 && !showModal ? (
          <div>
            <CalendarGrid
              events={events}
              onDayClick={handleDayClick}
              onEventClick={handleEventClick}
              onCreateEvent={handleCreate}
            />
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="text-center py-10 mt-6"
            >
              <div className="w-16 h-16 rounded-2xl bg-primary/10 dark:bg-primary/15 flex items-center justify-center mx-auto mb-5 shadow-soft-sm">
                <Calendar className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-base font-bold text-foreground mb-1">Your calendar is empty</h3>
              <p className="text-muted-foreground/70 text-sm max-w-xs mx-auto">
                Click any day on the calendar or use the <strong className="text-muted-foreground">+ Event</strong> button to get started
              </p>
            </motion.div>
          </div>
        ) : (
          <CalendarGrid
            events={events}
            onDayClick={handleDayClick}
            onEventClick={handleEventClick}
            onCreateEvent={handleCreate}
          />
        )}
      </div>

      {/* Event detail side panel */}
      <AnimatePresence>
        {selectedEvent && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/20 backdrop-blur-xs"
              onClick={() => setSelectedEvent(null)}
            />
            <motion.div
              initial={{ opacity: 0, x: 320 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 320 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-card/95 backdrop-blur-lg border-l border-border/60 shadow-soft-xl overflow-y-auto"
            >
              {/* Color accent bar */}
              <div className="h-2 w-full" style={{ backgroundColor: selectedEvent.color || DEFAULT_EVENT_COLOR }} />

              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-5">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-card-foreground m-0 leading-tight">{selectedEvent.title}</h2>
                    {selectedEvent.recurring && (
                      <span className="inline-flex items-center gap-1 mt-2 text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-lg font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                        Recurring
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all active:scale-90 ml-3"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Event details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm p-3 rounded-xl bg-muted/30 dark:bg-muted/15">
                    <div className="w-9 h-9 rounded-lg bg-muted/60 dark:bg-muted/30 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-foreground font-semibold">
                        {new Date(selectedEvent.start_time).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </div>
                      <div className="text-muted-foreground text-xs mt-0.5">
                        {formatTime(selectedEvent.start_time)} – {formatTime(selectedEvent.end_time)}
                      </div>
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="flex items-center gap-3 text-sm p-3 rounded-xl bg-muted/30 dark:bg-muted/15">
                      <div className="w-9 h-9 rounded-lg bg-muted/60 dark:bg-muted/30 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <span className="text-foreground font-medium">{selectedEvent.location}</span>
                    </div>
                  )}

                  {selectedEvent.child_name && (
                    <div className="flex items-center gap-3 text-sm p-3 rounded-xl bg-muted/30 dark:bg-muted/15">
                      <div className="w-9 h-9 rounded-lg bg-muted/60 dark:bg-muted/30 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <span className="text-foreground font-medium">{selectedEvent.child_name}</span>
                    </div>
                  )}
                </div>

                {selectedEvent.description && (
                  <div className="mt-6 pt-5 border-t border-border/60">
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Description</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed m-0">{selectedEvent.description}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6 pt-5 border-t border-border/60 flex gap-2.5">
                  <button
                    onClick={() => handleEditEvent(selectedEvent)}
                    className="btn btn-primary flex-1"
                  >
                    <Pencil className="w-4 h-4 mr-1.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteClick(selectedEvent.id)}
                    disabled={deletingId === selectedEvent.id}
                    className="btn btn-danger"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Event form modal */}
      {showModal && (
        <EventFormModal
          event={getPrefillEvent()}
          children={children}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingEvent(null); setPrefillDate(null); }}
        />
      )}

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {confirmDeleteId !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setConfirmDeleteId(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 8 }}
              transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
              className="bg-card rounded-2xl shadow-soft-xl w-full max-w-sm mx-4 p-6 border border-border/80"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-7 h-7 text-destructive" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-card-foreground text-center">Delete Event</h3>
              <p className="text-muted-foreground text-sm mb-6 text-center leading-relaxed">
                Are you sure you want to delete this event? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button className="btn btn-ghost flex-1 py-2.5" onClick={() => setConfirmDeleteId(null)}>
                  Cancel
                </button>
                <button className="btn btn-danger flex-1" onClick={handleDeleteConfirm}>
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageWrapper>
  );
};

export default CalendarPage;
